import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom';
import i18n from '@/i18n';
import { useAuth } from '@/store/auth';
import { Splash } from '@/components/Splash';
import { BottomNav } from '@/components/BottomNav';
import { TopBar } from '@/components/TopBar';
import { useStartParamRouter } from './useStartParamRouter';
import { useScreenViewTracking } from './useScreenViewTracking';

// Eager
import Onboarding from '@/screens/Onboarding';
import Feed from '@/screens/Feed';
import Shelf from '@/screens/Shelf';
import Topics from '@/screens/Topics';
import Friends from '@/screens/Friends';
import Profile from '@/screens/Profile';
import DailyLimit from '@/screens/DailyLimit';
import Book from '@/screens/Book';

// Lazy
const Subscription = lazy(() => import('@/screens/Subscription'));
const Comments = lazy(() => import('@/screens/Comments'));
const FriendShelf = lazy(() => import('@/screens/FriendShelf'));
const Topup = lazy(() => import('@/screens/Topup'));
const Withdrawal = lazy(() => import('@/screens/Withdrawal'));
const History = lazy(() => import('@/screens/History'));
const BooksHistory = lazy(() => import('@/screens/BooksHistory'));
const StreakCalendar = lazy(() => import('@/screens/StreakCalendar'));

function StartParamDispatcher() {
  useStartParamRouter();
  return null;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuth(s => s.status);
  const token = useAuth(s => s.token);
  const user = useAuth(s => s.user);
  const path = useLocation().pathname;

  if (status === 'loading' || (!token && status === 'idle')) {
    return <Splash />;
  }
  if (status === 'error' || !token || !user) {
    return <Splash error={i18n.t('errors.auth_failed')} />;
  }
  if ((!user.topics || user.topics.length < 3) && path !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <><StartParamDispatcher />{children}</>;
}

function ChromeLayout() {
  useScreenViewTracking();
  return (
    <div
      className="bg-bg flex flex-col overflow-hidden"
      style={{
        height: '100dvh',
        paddingTop: 'calc(var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)) + var(--tg-content-safe-area-inset-top, 0px) - 60px)',
        paddingLeft: 'calc(var(--tg-safe-area-inset-left, env(safe-area-inset-left, 0px)) + var(--tg-content-safe-area-inset-left, 0px))',
        paddingRight: 'calc(var(--tg-safe-area-inset-right, env(safe-area-inset-right, 0px)) + var(--tg-content-safe-area-inset-right, 0px))',
      }}
    >
      <TopBar />
      <main className="flex-1 flex flex-col min-h-0">
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}

const router = createBrowserRouter([
  { path: '/onboarding', element: <Onboarding /> },
  {
    path: '/subscription',
    element: (
      <AuthGate>
        <Suspense fallback={<Splash />}>
          <Subscription />
        </Suspense>
      </AuthGate>
    ),
  },
  {
    path: '/topup',
    element: (
      <AuthGate>
        <Suspense fallback={<Splash />}>
          <Topup />
        </Suspense>
      </AuthGate>
    ),
  },
  {
    path: '/withdrawal',
    element: (
      <AuthGate>
        <Suspense fallback={<Splash />}>
          <Withdrawal />
        </Suspense>
      </AuthGate>
    ),
  },
  {
    path: '/',
    element: (
      <AuthGate>
        <ChromeLayout />
      </AuthGate>
    ),
    children: [
      { index: true, element: <Feed /> },
      { path: 'shelf', element: <Shelf /> },
      { path: 'topics', element: <Topics /> },
      { path: 'friends', element: <Friends /> },
      { path: 'profile', element: <Profile /> },
      { path: 'limit', element: <DailyLimit /> },
      { path: 'book/:id', element: <Book /> },
      { path: 'card/:id/comments', element: <Suspense fallback={<Splash />}><Comments /></Suspense> },
      { path: 'users/:id/shelf', element: <Suspense fallback={<Splash />}><FriendShelf /></Suspense> },
      { path: 'profile/history', element: <Suspense fallback={<Splash />}><History /></Suspense> },
      { path: 'profile/books', element: <Suspense fallback={<Splash />}><BooksHistory /></Suspense> },
      { path: 'profile/streak', element: <Suspense fallback={<Splash />}><StreakCalendar /></Suspense> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
